/**
 * Keyword Scoring Utility
 *
 * Selects a diverse set of "highlight" keywords from a restaurant's keywords.
 * The algorithm prioritizes group diversity, picking from the most interesting
 * groups first (entertainment, views, beverages) before filling remaining slots
 * from groups with the most keywords.
 *
 * Used by the AtAGlance component to surface the most relevant keywords
 * without overwhelming the user.
 */

import type { UIMetadata, KeywordOption } from '@/types/uiMetadata';
import {
  getKeywordGroupKey,
  getKeywordDisplayName,
  getKeywordOption,
} from '@/api/queries/useConfigQueries';

// =============================================================================
// Types
// =============================================================================

export interface HighlightKeyword {
  /** The raw keyword key (e.g., 'live_music') */
  key: string;
  /** Human-readable display name (e.g., 'Live Music') */
  displayName: string;
  /** The keyword group this belongs to (e.g., 'entertainment') */
  group: string;
  /** Lucide icon name from the API, if available */
  icon?: string;
  /** Gradient start/end pair from the API, if available */
  gradient?: [string, string];
}

// =============================================================================
// Constants
// =============================================================================

/**
 * Group priority order from most interesting to least.
 * Keywords from groups listed earlier are selected first.
 */
const GROUP_PRIORITY = [
  'views_setting',
  'ambiance',
  'entertainment',
  'menu_specialties',
] as const;

// =============================================================================
// Core Algorithm
// =============================================================================

/**
 * Check whether a keyword has an icon in UI metadata.
 */
function hasIcon(key: string, uiMetadata: UIMetadata | undefined): boolean {
  return !!getKeywordOption(key, uiMetadata)?.icon;
}

/**
 * Sort keyword keys so those with icons come before those without.
 * Preserves relative order within each group (stable sort).
 */
function sortIconsFirst(keys: string[], uiMetadata: UIMetadata | undefined): string[] {
  return [...keys].sort((a, b) => {
    const aIcon = hasIcon(a, uiMetadata) ? 0 : 1;
    const bIcon = hasIcon(b, uiMetadata) ? 0 : 1;
    return aIcon - bIcon;
  });
}

/**
 * Pick the best keyword from a group, preferring keywords with icons.
 * Returns the picked keyword key and mutates the array by removing it.
 * If keywords with icons exist, picks the first one; otherwise picks the first keyword.
 */
function pickPreferringIcon(
  groupKeys: string[],
  uiMetadata: UIMetadata | undefined
): string {
  const iconIndex = groupKeys.findIndex(key => hasIcon(key, uiMetadata));
  if (iconIndex >= 0) {
    return groupKeys.splice(iconIndex, 1)[0];
  }
  // No icon keywords in this group; fall back to first available
  return groupKeys.shift()!;
}

/**
 * Select diverse highlight keywords from a restaurant's keywords.
 *
 * Algorithm:
 * 1. Group all keywords by their keyword group (from UI metadata).
 * 2. Pick 1 keyword from each group in GROUP_PRIORITY order (up to maxCount),
 *    preferring keywords that have icons in UI metadata.
 * 3. If slots remain, fill from groups with the most remaining keywords (round-robin),
 *    again preferring keywords with icons.
 * 4. Resolve display metadata (name, icon, gradient) for each selected keyword.
 * 5. Sort final results so icon keywords appear before non-icon keywords.
 *
 * Edge cases:
 * - 0 keywords: returns []
 * - Fewer keywords than maxCount: returns all resolved keywords
 * - All keywords in same group: takes multiple from that group
 * - Keywords not in any priority group: added at end if slots remain
 * - Groups where ALL keywords lack icons: still includes one (group coverage
 *   is more important than icon presence)
 *
 * @param keywords - Array of keyword keys from the restaurant
 * @param uiMetadata - UI metadata containing keyword groups and display info
 * @param maxCount - Maximum number of highlights to return (default: 6)
 * @returns Array of HighlightKeyword objects, icon keywords first then non-icon
 *
 * @example
 * ```tsx
 * const highlights = selectSmartHighlights(
 *   ['live_music', 'ocean_view', 'craft_cocktails', 'romantic', 'vegan_options'],
 *   uiMetadata,
 *   4
 * );
 * // Returns ~4 keywords, one from each group in priority order
 * ```
 */
export function selectSmartHighlights(
  keywords: string[],
  uiMetadata: UIMetadata | undefined,
  maxCount: number = 6
): HighlightKeyword[] {
  // Early exit: no keywords or no metadata to resolve against
  if (!keywords || keywords.length === 0) {
    return [];
  }

  // Step 1: Build a map of group -> keyword keys
  const groupMap = new Map<string, string[]>();
  const ungroupedKeys: string[] = [];

  for (const key of keywords) {
    const group = getKeywordGroupKey(key, uiMetadata);
    if (group) {
      if (!groupMap.has(group)) {
        groupMap.set(group, []);
      }
      groupMap.get(group)!.push(key);
    } else {
      ungroupedKeys.push(key);
    }
  }

  const selected: string[] = [];

  // Step 2: Pick 1 from each priority group in order, preferring keywords with icons
  for (const group of GROUP_PRIORITY) {
    if (selected.length >= maxCount) break;

    const groupKeys = groupMap.get(group);
    if (groupKeys && groupKeys.length > 0) {
      const pick = pickPreferringIcon(groupKeys, uiMetadata);
      selected.push(pick);

      // Remove group if exhausted
      if (groupKeys.length === 0) {
        groupMap.delete(group);
      }
    }
  }

  // Step 3: Fill remaining slots from groups with the most remaining keywords
  if (selected.length < maxCount) {
    // Collect all remaining grouped keywords, sorted by group size (descending)
    const remainingGroups = Array.from(groupMap.entries())
      .filter(([, keys]) => keys.length > 0)
      .sort((a, b) => b[1].length - a[1].length);

    // Round-robin through groups with remaining keywords, preferring icons
    let filled = true;
    while (selected.length < maxCount && filled) {
      filled = false;
      for (const [, keys] of remainingGroups) {
        if (selected.length >= maxCount) break;
        if (keys.length > 0) {
          selected.push(pickPreferringIcon(keys, uiMetadata));
          filled = true;
        }
      }
    }

    // If still room, add ungrouped keywords (icon-bearing first)
    const sortedUngrouped = sortIconsFirst(ungroupedKeys, uiMetadata);
    for (const key of sortedUngrouped) {
      if (selected.length >= maxCount) break;
      selected.push(key);
    }
  }

  // Step 4: Resolve display metadata for each selected keyword
  const resolved = selected.map(key => resolveHighlightKeyword(key, uiMetadata));

  // Step 5: Sort final results so icon keywords render before non-icon keywords
  resolved.sort((a, b) => {
    const aIcon = a.icon ? 0 : 1;
    const bIcon = b.icon ? 0 : 1;
    return aIcon - bIcon;
  });

  return resolved;
}

// =============================================================================
// Internal Helpers
// =============================================================================

/**
 * Resolve a keyword key into a fully populated HighlightKeyword object.
 * Looks up display name, icon, and gradient from UI metadata.
 */
function resolveHighlightKeyword(
  key: string,
  uiMetadata: UIMetadata | undefined
): HighlightKeyword {
  const option: KeywordOption | undefined = getKeywordOption(key, uiMetadata);
  const group = getKeywordGroupKey(key, uiMetadata) ?? 'other';
  const displayName = getKeywordDisplayName(key, uiMetadata);

  const highlight: HighlightKeyword = {
    key,
    displayName,
    group,
  };

  // Attach icon if the API provides one
  if (option?.icon) {
    highlight.icon = option.icon;
  }

  // Attach gradient if both start and end colors are available
  if (option?.gradientStart && option?.gradientEnd) {
    highlight.gradient = [option.gradientStart, option.gradientEnd];
  }

  return highlight;
}
